import { useEffect, useState } from "react";

import { formatPercent, formatSignedPercent } from "../lib/format";
import { CalibrationChart } from "../components/CalibrationChart";
import type { BacktestResults, LeagueAggregate } from "./backtestTypes";

type State =
  | { kind: "loading" }
  | { kind: "loaded"; data: BacktestResults }
  | { kind: "error"; message: string }
  | { kind: "missing" };

export function Backtest() {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    fetch("/data/backtest/results.json", { headers: { accept: "application/json" } })
      .then(async (res) => {
        if (!alive) return;
        if (res.status === 404) {
          setState({ kind: "missing" });
          return;
        }
        if (!res.ok) {
          setState({ kind: "error", message: `HTTP ${res.status}` });
          return;
        }
        const data = (await res.json()) as BacktestResults;
        setState({ kind: "loaded", data });
      })
      .catch((err: Error) => {
        if (!alive) return;
        setState({ kind: "error", message: err.message });
      });
    return () => {
      alive = false;
    };
  }, []);

  if (state.kind === "loading") return <Skeleton />;
  if (state.kind === "missing")
    return (
      <div className="panel p-5 text-sm text-ink-500">
        <p className="font-semibold text-ink-900">Backtest results not generated yet.</p>
        <p className="mt-1">
          Run <code className="rounded bg-ink-50 px-1 text-[11px]">npm run backtest</code> to produce{" "}
          <code className="rounded bg-ink-50 px-1 text-[11px]">data/backtest/results.json</code>.
        </p>
      </div>
    );
  if (state.kind === "error")
    return (
      <div className="panel border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Could not load backtest results: {state.message}
      </div>
    );

  return <BacktestView data={state.data} />;
}

function BacktestView({ data }: { data: BacktestResults }) {
  const overall = data.overall;
  const ciLower = overall.ci.lower;
  const ciUpper = overall.ci.upper;

  return (
    <div className="space-y-3">
      <header className="panel space-y-2 p-4">
        <h2 className="text-base font-semibold text-ink-900">Has this algorithm actually worked?</h2>
        <p className="text-xs leading-snug text-ink-700">
          We replayed the same picking rules on <strong>{data.matchesParsed.toLocaleString()}</strong> real
          historical matches across <strong>{data.leagues.length} top European leagues</strong> over{" "}
          <strong>{data.seasons.length} seasons</strong>, and pretended to bet €1 on every pick. The numbers
          below show what would have happened.
        </p>
        <p className="text-xs leading-snug text-ink-500">
          Generated {formatDate(data.generatedAt)}. {data.caveat}
        </p>
      </header>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Bets placed"
          value={overall.roi.totalPicks.toLocaleString()}
          hint="how many picks survived the filters across all 5 seasons"
        />
        <Kpi
          label="Win rate"
          value={formatPercent(overall.roi.hitRate)}
          hint="% of those bets that won outright"
        />
        <Kpi
          label="Profit per €1"
          value={formatSignedPercent(overall.roi.roi)}
          hint={`95% sure the true value is between ${formatSignedPercent(ciLower)} and ${formatSignedPercent(ciUpper)}`}
          tone={overall.roi.roi > 0 ? "good" : "bad"}
        />
        <Kpi
          label="Closing line value"
          value={formatSignedPercent(overall.clv.clv)}
          hint="how much sharper / softer our entry price was vs the closing market (Pinnacle). Positive over thousands of bets = real edge."
          tone={overall.clv.clv > 0 ? "good" : "bad"}
        />
      </div>

      <div className="panel p-4 text-xs leading-snug text-ink-700">
        <p>
          <strong>How to read the headline number:</strong> picking simple favorites on consumer bookmaker
          odds is a known losing game — over the long run you bleed exactly the bookmaker&apos;s mark-up. A
          result around <strong>−4% to −8% ROI</strong> is the expected floor; anything above that is doing
          better than &quot;just paying the vig&quot;. To actually make money you need the{" "}
          <strong>+EV badges</strong> on the Singles tab — they fire when our consensus data says a bet is
          mispriced in your favor.
        </p>
      </div>

      {data.modelPerformance ? <ModelPerformanceSection data={data} /> : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="panel p-4">
          <h3 className="text-sm font-semibold text-ink-900">Is the probability estimate honest?</h3>
          <p className="text-[11px] leading-snug text-ink-500">
            Each dot groups picks where we predicted a similar win chance. The dot&apos;s position on the
            vertical axis is the % that actually won. If we&apos;re calibrated, dots sit on the diagonal line:
            when we say 70%, the bet wins 70% of the time. Bubble size shows how many bets are in that bucket.
          </p>
          <div className="mt-2">
            <CalibrationChart buckets={overall.calibration} />
          </div>
        </section>

        <section className="panel p-4">
          <h3 className="text-sm font-semibold text-ink-900">Scoring details</h3>
          <p className="text-[11px] leading-snug text-ink-500">
            Two academic metrics for the curious. <em>Log-loss</em> punishes confident wrong calls harder than
            soft ones — lower is better. <em>Brier</em> is a simpler squared-error version. Both confirm
            calibration.
          </p>
          <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <DlRow label="Log-loss" value={overall.logLoss.toFixed(4)} />
            <DlRow label="Brier score" value={overall.brier.toFixed(4)} />
            <DlRow
              label="Doubles correlation"
              value={`${data.doublesCorrelation.ratio.toFixed(4)} (${data.doublesCorrelation.pairs.toLocaleString()} pairs)`}
            />
            <DlRow
              label="Doubles math"
              value={
                Math.abs(data.doublesCorrelation.ratio - 1) < 0.02
                  ? "Independence safe ✓"
                  : "Adjusted for correlation"
              }
            />
          </dl>
          <p className="mt-2 text-[11px] leading-snug text-ink-500">
            The &quot;doubles correlation&quot; check confirms it&apos;s safe to multiply two single-pick
            probabilities together — i.e. the two matches don&apos;t share hidden common factors.
          </p>
        </section>
      </div>

      <section className="panel p-4">
        <h3 className="text-sm font-semibold text-ink-900">Per-league rollup</h3>
        <div className="-mx-1 mt-2 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-ink-500">
                <th className="px-1 py-1 text-left">League</th>
                <th className="px-1 py-1 text-right">Picks</th>
                <th className="px-1 py-1 text-right">Hit rate</th>
                <th className="px-1 py-1 text-right">ROI</th>
                <th className="px-1 py-1 text-right">95% CI</th>
                <th className="px-1 py-1 text-right">CLV</th>
              </tr>
            </thead>
            <tbody>
              {data.perLeague.map((row) => (
                <PerLeagueRow row={row} key={row.league} />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ModelPerformanceSection({ data }: { data: BacktestResults }) {
  const mp = data.modelPerformance!;
  const m = mp.model.roi;
  const mci = mp.model.ci;
  const h = mp.highConviction.roi;
  const hci = mp.highConviction.ci;
  const baselineRoi = data.overall.roi.roi;
  const liftVsBaseline = h.roi - baselineRoi;

  return (
    <section className="panel space-y-3 p-4">
      <header className="space-y-1">
        <h3 className="text-sm font-semibold text-ink-900">
          Does the independent model actually find better bets?
        </h3>
        <p className="text-[11px] leading-snug text-ink-500">
          We re-ran the same picks through our independent Dixon-Coles model (rolling 10-match attack/defence
          per team — no bookmaker prices in the loop). Then we isolated the strict subset where the model AND
          the closing line BOTH say the bet is +EV vs Bet365, and where their probability estimates agree
          within {formatPercent(data.agreementThreshold ?? 0.04)}. This is the offline analog of the live{" "}
          <strong>★ HIGH CONVICTION</strong> badge.
        </p>
        <p className="text-[11px] leading-snug text-ink-500">
          {mp.matchesWithModel.toLocaleString()} matches had enough team history (≥ 5 prior matches per side)
          for the model to fire.
        </p>
      </header>

      <div className="grid gap-2 sm:grid-cols-3">
        <Kpi
          label="Baseline ROI"
          value={formatSignedPercent(baselineRoi)}
          hint={`favourite-by-NT, n=${data.overall.roi.totalPicks.toLocaleString()}`}
          tone={baselineRoi > 0 ? "good" : "bad"}
        />
        <Kpi
          label="Model-only ROI"
          value={formatSignedPercent(m.roi)}
          hint={`re-evaluated with model probability, n=${m.totalPicks.toLocaleString()} (95% CI ${formatSignedPercent(mci.lower)}…${formatSignedPercent(mci.upper)})`}
          tone={m.roi > 0 ? "good" : "bad"}
        />
        <Kpi
          label="High-conviction ROI"
          value={formatSignedPercent(h.roi)}
          hint={`n=${h.totalPicks.toLocaleString()} (95% CI ${formatSignedPercent(hci.lower)}…${formatSignedPercent(hci.upper)})`}
          tone={h.roi > 0 ? "good" : "bad"}
        />
      </div>

      <div
        className={`rounded p-2.5 text-[11px] leading-snug ${
          liftVsBaseline > 0 ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"
        }`}
      >
        High-conviction picks improve ROI by <strong>{formatSignedPercent(liftVsBaseline)}</strong> over the
        baseline (point estimate; {h.totalPicks} bets across {data.seasons.length} seasons ×{" "}
        {data.leagues.length} leagues). Wide CI because the subset is small by design — these are the rare
        matches where two independent signals line up. Hit rate: <strong>{formatPercent(h.hitRate)}</strong>{" "}
        vs <strong>{formatPercent(data.overall.roi.hitRate)}</strong> baseline.
      </div>

      {mp.perLeague.some((row) => row.roi.totalPicks > 0) ? (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
            Per-league high-conviction
          </p>
          <div className="-mx-1 mt-2 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-ink-500">
                  <th className="px-1 py-1 text-left">League</th>
                  <th className="px-1 py-1 text-right">Picks</th>
                  <th className="px-1 py-1 text-right">Hit</th>
                  <th className="px-1 py-1 text-right">ROI</th>
                  <th className="px-1 py-1 text-right">95% CI</th>
                </tr>
              </thead>
              <tbody>
                {mp.perLeague
                  .filter((row) => row.roi.totalPicks > 0)
                  .map((row) => (
                    <tr key={row.league} className="border-t border-ink-100 text-ink-700">
                      <td className="px-1 py-1.5">
                        <span className="font-semibold text-ink-900">{row.league}</span>{" "}
                        <span className="text-ink-500">{row.label}</span>
                      </td>
                      <td className="px-1 py-1.5 text-right">{row.roi.totalPicks}</td>
                      <td className="px-1 py-1.5 text-right">{formatPercent(row.roi.hitRate)}</td>
                      <td
                        className={`px-1 py-1.5 text-right font-semibold ${
                          row.roi.roi > 0 ? "text-emerald-700" : "text-rose-700"
                        }`}
                      >
                        {formatSignedPercent(row.roi.roi)}
                      </td>
                      <td className="px-1 py-1.5 text-right text-ink-500">
                        {formatSignedPercent(row.ci.lower)} / {formatSignedPercent(row.ci.upper)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function PerLeagueRow({ row }: { row: LeagueAggregate }) {
  return (
    <tr className="border-t border-ink-100 text-ink-700">
      <td className="px-1 py-1.5">
        <span className="font-semibold text-ink-900">{row.league}</span>{" "}
        <span className="text-ink-500">{row.label}</span>
      </td>
      <td className="px-1 py-1.5 text-right">{row.roi.totalPicks.toLocaleString()}</td>
      <td className="px-1 py-1.5 text-right">{formatPercent(row.roi.hitRate)}</td>
      <td
        className={`px-1 py-1.5 text-right font-semibold ${row.roi.roi > 0 ? "text-emerald-700" : "text-rose-700"}`}
      >
        {formatSignedPercent(row.roi.roi)}
      </td>
      <td className="px-1 py-1.5 text-right text-ink-500">
        {formatSignedPercent(row.ci.lower)} / {formatSignedPercent(row.ci.upper)}
      </td>
      <td className={`px-1 py-1.5 text-right ${row.clv.clv > 0 ? "text-emerald-700" : "text-rose-700"}`}>
        {formatSignedPercent(row.clv.clv)}
      </td>
    </tr>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "good" | "bad";
}) {
  const toneClass = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-rose-700" : "text-ink-900";
  return (
    <div className="panel p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">{label}</p>
      <p className={`text-xl font-semibold leading-tight ${toneClass}`}>{value}</p>
      {hint ? <p className="text-[11px] text-ink-500">{hint}</p> : null}
    </div>
  );
}

function DlRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-right font-semibold text-ink-900">{value}</dd>
    </>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3">
      <div className="panel h-20 animate-pulse bg-ink-50" />
      <div className="grid gap-2 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="panel h-20 animate-pulse bg-ink-50" />
        ))}
      </div>
      <div className="panel h-60 animate-pulse bg-ink-50" />
    </div>
  );
}

function formatDate(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Oslo",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}
